import React, { useState } from 'react';
import styled from 'styled-components';
import type { ProcessingActivity, TurnStatus } from '../../../hooks/useWorkspace';
import ProcessingActivityFeed from '../ProcessingActivityFeed';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const CollapsedPill = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  font-size: 0.75rem;
  color: var(--text-secondary);
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover {
    background: var(--accent-subtle, rgba(99, 102, 241, 0.08));
    color: var(--text-primary);
  }
`;

const CollapseToggle = styled.button`
  align-self: flex-start;
  padding: 0.125rem 0.5rem;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.75rem;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    color: var(--text-secondary);
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-block;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.15s;
  font-size: 0.625rem;
`;

function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
}

interface TurnActivityStripProps {
  activities: ProcessingActivity[];
  status: TurnStatus;
  startTime: number;
  durationMs?: number;
}

/**
 * Renders the per-turn activity strip. While the turn is running or awaiting
 * input, it expands into the full {@link ProcessingActivityFeed}. Once the
 * turn completes, it collapses into a single "Worked for Ns · N steps" pill
 * that can be expanded to inspect the trace.
 */
const TurnActivityStrip: React.FC<TurnActivityStripProps> = ({
  activities,
  status,
  startTime,
  durationMs,
}) => {
  const completed = status === 'completed' || status === 'failed';
  const [expanded, setExpanded] = useState(!completed);

  if (activities.length === 0 && !completed) return null;
  if (activities.length === 0) return null;

  const stepCount = activities.length;
  const elapsed = completed
    ? durationMs ?? Date.now() - startTime
    : Date.now() - startTime;

  if (!expanded) {
    return (
      <Wrap>
        <CollapsedPill
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="Expand activity trace"
        >
          <span aria-hidden="true">&#9881;</span>
          <span>
            Worked for {formatElapsed(elapsed)} &middot; {stepCount} step
            {stepCount === 1 ? '' : 's'}
          </span>
          <Caret $open={false}>&#9656;</Caret>
        </CollapsedPill>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <ProcessingActivityFeed
        activities={activities}
        startTime={completed ? null : startTime}
      />
      {completed && (
        <CollapseToggle
          type="button"
          onClick={() => setExpanded(false)}
          aria-label="Collapse activity trace"
        >
          <Caret $open={true}>&#9656;</Caret> Collapse activity
        </CollapseToggle>
      )}
    </Wrap>
  );
};

export default TurnActivityStrip;
